import type { Messages } from '../catalogue.en'

/**
 * Laboratory: moving between a patient's result surfaces, and everything those
 * surfaces say when they are not showing results.
 *
 * Composed into the public catalogue by `catalogue.he.ts`. Nothing imports this
 * module directly: consumers ask the catalogue, so how the messages are stored
 * stays this layer's business.
 */
export const laboratory: Partial<Messages> = {
  "lab.correction.reviewTitle": "תיקון דוח מעבדה מאושר",
  "lab.correction.confirmRecords": "האישור שומר דוח מתוקן מלא ואת הסיבה שלך. הערכים המקוריים נשמרים בהיסטוריה.",
  "lab.correction.reason": "סיבת התיקון",
  "lab.correction.history": "דוחות מאושרים והיסטוריה",
  "lab.correction.sourceChanged": "הדוח כבר תוקן או סומן כשגוי. הטיוטה שלך נשמרה; יש לפתוח את הגרסה העדכנית לפני התיקון.",
  "lab.correction.report": "דוח מעבדה",
  "lab.correction.recorded": "מועד הרישום",
  "lab.correction.status": "מצב עדכני",
  "lab.correction.empty": "אין דוחות מעבדה מאושרים",
  "lab.correction.recordedOn": "נרשם: {when}",
  "lab.correction.collectedOn": "נאסף: {when}",
  "lab.correction.reportedOn": "דווח: {when}",
  "lab.correction.versions": "גרסאות הדוח",
  "lab.correction.version": "{revision} · {when} · {status}",
  "lab.correction.seedRollover": "עדכון תאריכי נתוני הדגמה",
  "lab.correction.originalValues": "הערכים שנרשמו בגרסה זו",
  "lab.correction.sourceEvidence": "המידע מהמקור המקורי",
  "lab.correction.start": "תיקון הדוח",
  "lab.correction.resume": "המשך טיוטת תיקון {number}",
  "lab.correction.enterError": "סימון כרישום שגוי",
  "lab.correction.errorNotice": "הפעולה מסירה את הדוח מהתוצאות וההתראות העדכניות. הדוח המקורי והסיבה שלך נשמרים בהיסטוריה.",
  "lab.correction.confirmError": "אישור רישום שגוי",
  "lab.correction.cancel": "ביטול",
  "lab.correction.current": "דוח קליני עדכני",
  "lab.correction.superseded": "הוחלף · היסטוריה בלבד",
  "lab.correction.erroneous": "רישום שגוי · היסטוריה בלבד",
  "lab.correction.unknownStatus": "מצב לא מוכר · לא ניתן לתקן",
  // The combined workspace name (#194). "Vital Signs" is the shipped MoH
  // glossary term סימנים חיוניים; never a coined alternative.
  'lab.title': 'תוצאות מעבדה וסימנים חיוניים',

  'lab.menuLabel': 'מקטעי מעבדה וסימנים חיוניים',
  'lab.navGraphs': 'גרפים',
  'lab.navGraphsDescription': 'מגמות תוצאות לאורך זמן',
  'lab.navTable': 'טבלה',
  'lab.navTableDescription': 'מטריצת התוצאות, שורה לכל בדיקת מעבדה',
  'lab.navFiles': 'קבצים',
  'lab.navFilesDescription': 'קובצי דוחות מעבדה',
  'lab.report.back': 'חזרה',
  'lab.report.enlarge': 'הגדלת מסמך',
  'lab.navImports': 'דוחות',
  'lab.navImportsDescription': 'תוצאות שמורות, מקורות והיסטוריית גרסאות',

  'lab.forbidden': 'אין לך הרשאה לצפות בתוצאות מעבדה או בסימנים חיוניים',
  'lab.forbiddenDetail': 'שאר כרטיס המטופל עדיין זמין.',
  'lab.forbiddenLaboratoryOnly': 'תצוגה זו דורשת הרשאת קריאה של תוצאות מעבדה',

  'lab.graphs.listLabel': 'מגמות מעבדה וסימנים חיוניים',
  'lab.files.tableLabel': 'קובצי תוצאות מעבדה',

  // The point detail on the trend chart and the files table. The basic results
  // table these once headed is gone — the Table section speaks `lab.matrix.*`.
  'lab.column.value': 'ערך',
  'lab.column.reference': 'טווח ייחוס',
  'lab.column.level': 'רמה',
  'lab.column.file': 'קובץ',
  'lab.column.recorded': 'תועד',

  'lab.graphs.loading': 'טוען מגמות מעבדה',
  'lab.graphs.loadFailed': 'לא ניתן היה לטעון את מגמות המעבדה',
  'lab.graphs.empty': 'אין מגמות להצגה בתקופה שנבחרה',
  'lab.files.loading': 'טוען קובצי תוצאות',
  'lab.files.loadFailed': 'לא ניתן היה לטעון את קובצי התוצאות',
  'lab.files.empty': 'אין קובצי תוצאות בתקופה שנבחרה',
  'lab.files.filteredEmpty': 'אין קובצי תוצאות התואמים לסינון הזה',
  'lab.files.filteredEmptyDetail': 'אפס את הסינון כדי לראות את כל קובצי התוצאות בתקופה שנבחרה.',

  'lab.imports.intro': 'חלצו דוח מעבדה שעבר הסרת פרטים מזהים, אמתו כל ערך מול המקור ולאחר מכן אשרו אותו ברשומת המטופל.',
  'lab.imports.readOnly': 'ניתן לקרוא ייבואים אך לא לבדוק אותם',
  'lab.imports.readOnlyDetail': 'אישור תוצאות שחולצו מחייב הרשאת כתיבה למעבדה.',
  'lab.imports.extractFailed': 'לא ניתן היה לחלץ את המסמך. יש לבדוק את ספק הבינה המלאכותית שהוגדר ולנסות שוב.',
  'lab.imports.loading': 'ייבואי המעבדה נטענים',
  'lab.imports.unavailable': 'ייבואי המעבדה אינם זמינים',
  'lab.imports.empty': 'עדיין אין ייבואי מעבדה',
  'lab.imports.listLabel': 'ייבואי מעבדה',
  'lab.imports.unknownLaboratory': 'דוח מעבדה',
  'lab.imports.unknownSource': 'מסמך המקור',
  'lab.imports.sourceGone': 'מסמך המקור אינו זמין עוד',
  'lab.imports.selectionGone': 'הייבוא שבדקתם אינו מופיע עוד ברשימה הזו.',
  'lab.imports.selectionGoneDetail': 'בחרו אחד מהייבואים המופיעים כאן כדי להמשיך.',
  'lab.imports.analysing': 'קריאת עמודים',
  'lab.imports.pagesReview': 'בחירת עמודים',
  'lab.imports.recognizing': 'בזיהוי',
  'lab.imports.draft': 'נדרש אימות',
  'lab.imports.committed': 'אושר',
  'lab.imports.cancelled': 'בוטל',
  'lab.imports.failed': 'הזיהוי נכשל',
  'lab.imports.reviewTitle': 'אימות קליני',
  'lab.imports.openSource': 'פתיחת מסמך המקור',

  'lab.file.title': 'תוצאות מעבדה',
  'lab.file.close': 'סגירה',
  'lab.file.draftWaiting': 'הערכים שזוהו ממתינים לבדיקתך.',
  'lab.file.saved': 'התוצאות מדוח זה נרשמו בתיק.',
  'lab.file.discarded': 'קריאה קודמת של הדוח הזה בוטלה.',
  'lab.file.savedOn': 'התוצאות מדוח זה אושרו {when}.',
  'lab.file.unavailable': 'לא ניתן לקרוא את טיוטות המעבדה של המטופל.',
  'lab.imports.documentPane': 'מסמך המקור',
  'lab.imports.valuesPane': 'תוצאות שזוהו',
  'lab.imports.confirmRecords':
    'האישור ישמור את הערכים שנכללו עם מועדי האיסוף או הדיווח שהוקצו להם. הם יסומנו כמזוהים מסריקה זו, בצירוף שמך ומועד האישור.',
  'lab.imports.warnings': 'אזהרות החילוץ',
  'lab.imports.laboratory': 'מעבדה',
  'lab.imports.collectedAt': 'מועד איסוף',
  'lab.imports.reportedAt': 'מועד דיווח',
  'lab.imports.include': 'הכללה',
  'lab.imports.panel': 'שם הפאנל המודפס',
  'lab.imports.test': 'שם המדד המודפס',
  'lab.imports.value': 'הערך המודפס',
  'lab.imports.unit': 'היחידה המודפסת',
  'lab.imports.reference': 'טווח ייחוס',
  'lab.imports.referenceLow': 'גבול תחתון',
  'lab.imports.referenceHigh': 'גבול עליון',
  'lab.imports.interpretation': 'פירוש',
  'lab.imports.specimenMethod': 'דגימה ושיטה',
  'lab.imports.specimen': 'הדגימה המודפסת',
  'lab.imports.method': 'השיטה המודפסת',
  'lab.imports.coding': 'קוד בדיקה',
  'lab.imports.ucum': 'יחידת UCUM',
  'lab.imports.ucumValue': 'יחידת UCUM: {code}',
  'lab.imports.notSet': 'לא הוגדר',
  'lab.imports.codingUnmapped': 'ללא מיפוי',
  'lab.imports.addRow': 'הוספת תוצאה שהוחמצה',
  'lab.imports.save': 'שמירת הטיוטה',
  'lab.imports.saving': 'שומר…',
  'lab.imports.confirm': 'אישור התוצאות',
  'lab.imports.confirming': 'מאשר…',
  'lab.imports.saveFailed': 'לא ניתן היה לשמור את הטיוטה. יש לטעון את הטיוטה העדכנית ולנסות שוב.',
  'lab.imports.confirmFailed': 'לא ניתן היה לאשר את התוצאות. יש להשלים את הערכים הנדרשים ולנסות שוב.',
  'lab.imports.changedConcurrently': 'הטיוטה השתנתה במקום אחר. יש לטעון אותה מחדש לפני החלת השינויים.',
  'lab.imports.noLongerDraft': 'הייבוא כבר אושר ולא ניתן עוד לערוך אותו.',
  'lab.imports.openAIKeyMissing': 'נדרש מפתח OpenAI של החשבון לצורך החילוץ. יש להוסיף אותו בהגדרות החשבון.',
  'lab.imports.invalid': 'יש לבדוק את התאריכים, שמות הבדיקות, הערכים וקודי המינוח הנדרשים.',
  'lab.imports.forbidden': 'אין לך הרשאה לבצע את פעולת ייבוא המעבדה הזו.',
  'lab.imports.gone': 'ייבוא זה או מסמך המקור שלו אינם זמינים עוד.',
  'lab.imports.confirmedNotice': 'הערכים שנבדקו נרשמו כתוצאות מעבדה סופיות.',

  'lab.omitted.undatedPoints': '{count} תוצאות מעבדה ללא תאריך אינן מוצגות בתרשים',
  'lab.omitted.emptyValues': '{count} תוצאות מעבדה ללא ערך אינן מוצגות בתרשים',
  'lab.omitted.vitalUndated': '{count} מדידות סימנים חיוניים ללא תאריך אינן מוצגות בתרשים',
  'lab.omitted.vitalTextValues': '{count} מדידות סימנים חיוניים עם ערך טקסט בלבד אינן מוצגות בתרשים',

  'lab.partial.laboratory': 'תוצאות המעבדה אינן זמינות כעת; הסימנים החיוניים מוצגים.',
  'lab.partial.vitals': 'הסימנים החיוניים אינם זמינים כעת; תוצאות המעבדה מוצגות.',

  'lab.criticalResult': 'תוצאה קריטית',

  /*
   * The trend chart. Two adaptations against the Teris Hebrew, both on
   * purpose: "clinical events" became result events, because the rail draws
   * the result timeline here; and the date-source label says מדד where Teris
   * said the bare examination word. `tests/no-compiled-protocol` proves the
   * published SOAP protocol is not compiled into the bundle by substring-
   * searching the built files for its labels, and one of them IS that bare
   * word — so no laboratory copy in this file may contain it.
   */
  'lab.trend.subtitle': 'מגמות מדדי מעבדה וסימנים חיוניים, ערכי ייחוס ואירועי התוצאות',
  'lab.trend.summary': 'סיכום התרשים',
  'lab.trend.indicatorsCount': '{count} מדדים',
  'lab.trend.pointsCount': '{count} נקודות',
  'lab.trend.criticalCount': '{count} קריטיים',
  'lab.trend.refreshing': 'הנתונים מתרעננים',
  'lab.trend.controls': 'הגדרות התרשים',
  'lab.trend.period': 'תקופה',
  'lab.trend.presets': 'בחירת תקופה מהירה',
  'lab.trend.preset7': "7 ימ'",
  'lab.trend.preset30': "30 ימ'",
  'lab.trend.preset90': "90 ימ'",
  'lab.trend.presetAll': 'הכול',
  'lab.trend.dateFrom': 'מתאריך',
  'lab.trend.dateTo': 'עד תאריך',
  'lab.trend.resetPeriod': 'איפוס התקופה',
  'lab.trend.compare': 'השוואה לתקופה הקודמת',
  'lab.trend.compareHint': 'הגדירו את שני תאריכי התקופה',
  'lab.trend.comparePrev': 'התקופה הקודמת',
  'lab.trend.compareLoading': 'התקופה הקודמת נטענת',
  'lab.trend.compareError': 'התקופה הקודמת אינה זמינה',
  'lab.trend.display': 'תצוגה',
  'lab.trend.search': 'חיפוש',
  'lab.trend.searchPlaceholder': 'מציאת מדד',
  'lab.trend.selectAll': 'בחירת הכול',
  'lab.trend.clearSelection': 'ניקוי הבחירה',
  'lab.trend.showReference': 'ערכי ייחוס',
  'lab.trend.showCritical': 'סימוני קריטיות',
  'lab.trend.showValues': 'תוויות הערכים',
  'lab.trend.showEvents': 'אירועי התוצאות',
  'lab.trend.indicators': 'מדדים',
  'lab.trend.selectedCount': '{count} נבחרו',
  'lab.trend.selectionEmpty': 'בחרו מדדים לתרשים',
  'lab.trend.logScale': 'סולם לוגריתמי',
  'lab.trend.logShort': 'לוג',
  'lab.trend.criticalMode': 'תצוגת קריטיים',
  'lab.trend.critRing': 'טבעת',
  'lab.trend.critFlag': 'דגל',
  'lab.trend.critBand': 'רצועה',
  'lab.trend.qualDetected': 'זוהה',
  'lab.trend.qualNotDetected': 'לא זוהה',
  'lab.trend.qualPositive': 'חיובי',
  'lab.trend.qualNegative': 'שלילי',
  'lab.trend.qualDoubtful': 'מוטל בספק',
  'lab.trend.latest': 'אחרון',
  'lab.trend.latestDate': 'תאריך',
  'lab.trend.eventsTitle': 'אירועי התוצאות',
  'lab.trend.eventCluster': '{date}: {events}',
  'lab.trend.eventSubject': '{kind}, {subject}',
  'lab.trend.eventsCount': '{count} אירועים',
  'lab.trend.eventsLoading': 'אירועי התוצאות נטענים',
  'lab.trend.eventsUnavailable': 'אירועי התוצאות אינם זמינים',
  'lab.trend.legend': 'מקרא החריגות',
  'lab.trend.noGroup': 'ללא קבוצה',
  'lab.trend.detailGroup': 'קבוצה',
  'lab.trend.detailSource': 'מקור התאריך',
  'lab.trend.sourceItem': 'תאריך שורת המדד',
  'lab.trend.sourceResult': 'תאריך התוצאה',
  'lab.trend.sourceCollection': 'תאריך נטילת הדגימה',

  /*
   * The results matrix, in the Teris Hebrew. Its own copy already says מדד
   * for the analyte column and search, and the plural בדיקות in the title and
   * empty state does not contain the bare singular the `no-compiled-protocol`
   * gate searches for (see `lab.trend.subtitle` above).
   */
  'lab.matrix.title': 'טבלת סיכום בדיקות וסימנים חיוניים',
  'lab.matrix.colTest': 'מדד',
  'lab.matrix.colUnit': "יח'",
  'lab.matrix.colNorm': 'ערך ייחוס',
  'lab.matrix.noGroup': 'ללא קבוצה',
  'lab.matrix.filters': 'מסנני טבלת הסיכום',
  'lab.matrix.dateRange': 'טווח תאריכים',
  'lab.matrix.filterTests': 'מדדים',
  'lab.matrix.filterGroups': 'קבוצות',
  'lab.matrix.filterFamilies': 'מקורות',
  'lab.matrix.searchPlaceholder': 'חיפוש מדד...',
  'lab.matrix.selectedCount': 'נבחרו: {count}',
  'lab.matrix.criticalOnly': 'קריטיים בלבד',
  'lab.matrix.dateFrom': 'מתאריך',
  'lab.matrix.dateTo': 'עד תאריך',
  'lab.matrix.reset': 'איפוס',
  'lab.matrix.empty': 'לא נמצאו בדיקות',
  'lab.matrix.undated': 'ללא תאריך',
  'lab.matrix.critical': 'ערך קריטי',
  'lab.matrix.recognized': 'זוהה מסריקת דוח ואושר',
  'lab.matrix.recognizedOn': 'זוהה מסריקת דוח, אושר {when}',
  'lab.matrix.loading': 'הטבלה נטענת',
  'lab.matrix.unavailable': 'הטבלה אינה זמינה',

  'lab.level.critLow': 'נמוך קריטי',
  'lab.level.low': 'נמוך',
  'lab.level.normal': 'תקין',
  'lab.level.unknown': 'חריג',
  'lab.level.high': 'גבוה',
  'lab.level.critHigh': 'גבוה קריטי',
  'lab.level.none': 'ללא הערכת ייחוס',

  // The vital-parameter half of the combined workspace (#194). "Vital Signs"
  // stays the shipped glossary term סימנים חיוניים throughout.
  'lab.vital.group': 'סימנים חיוניים',
  'lab.vital.unmapped': 'לא הותאם למינוח הייחוס',
  'lab.family.laboratory': 'מעבדה',
  'lab.family.vitalMapped': 'סימנים חיוניים מותאמים',
  'lab.family.vitalUnmapped': 'סימנים חיוניים לא מותאמים',
  'lab.vital.detailStatus': 'סטטוס',
  'lab.vital.detailSourceKind': 'מקור',
  'lab.vital.statusPreliminary': 'ראשונית',
  'lab.vital.statusFinal': 'סופית',
  'lab.vital.statusAmended': 'תוקנה',
  'lab.vital.statusCancelled': 'בוטלה',
  'lab.vital.statusEnteredInError': 'הוזנה בטעות',
  'lab.vital.sourceManual': 'הוזן ידנית',
  'lab.vital.sourceDevice': 'נמדד במכשיר',
  'lab.vital.sourceIntegration': 'התקבל מאינטגרציה',
  'lab.vital.sourceDerived': 'ערך נגזר',
  'lab.vital.sourceSeed': 'נתוני אתחול',
  'lab.vital.timeSourceSource': 'זמן המדידה מהמקור',
  'lab.vital.timeSourceRecorded': 'זמן התיעוד',
  'lab.vital.timeSourceDerived': 'נגזר מנתוני המקור',
  'lab.vital.timeSourceAbsent': 'אין תאריך במקור',

  'lab.status.parsed': 'נקלטה',
  'lab.status.superseded': 'הוחלפה',
  'lab.status.failed': 'נכשלה',

  'lab.valueKind.numeric': 'מספרי',
  'lab.valueKind.qualitative': 'איכותני',
  'lab.valueKind.empty': 'ללא ערך',

  'lab.event.collected': 'הדגימה נלקחה',
  'lab.event.received': 'הדגימה התקבלה',
  'lab.event.finalized': 'התוצאה הסתיימה',
  'lab.event.corrected': 'התוצאה תוקנה',
  'lab.event.superseded': 'התוצאה הוחלפה',
  'lab.event.enteredInError': 'הוזן בטעות',
}
